import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X, Loader2, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TYPE_META } from "@/components/layers";

const ICON_TYPE = { animation: "text" };

const Stars = ({ n }) => (
  <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3 w-3 ${i <= Math.round(n / 20) ? "fill-[#5FA82E] text-[#5FA82E]" : "text-[#2E2E2E]"}`} />)}</span>
);

export const AiPanel = ({ bookId, onAccept }) => {
  const [state, setState] = useState({ ai_status: "idle", suggestions: [] });

  const load = useCallback(() => api.get(`/books/${bookId}/ai`).then((r) => setState(r.data)), [bookId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (state.ai_status !== "running") return;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [state, load]);

  const analyze = async () => {
    try {
      await api.post(`/books/${bookId}/ai/analyze`);
      setState({ ai_status: "running", suggestions: [] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const mark = async (s, status) => {
    setState((st) => ({ ...st, suggestions: st.suggestions.map((x) => (x.id === s.id ? { ...x, status } : x)) }));
    await api.patch(`/books/${bookId}/ai/${s.id}`, { status });
    if (status === "accepted") onAccept(s);
  };

  const pending = state.suggestions.filter((s) => s.status === "pending");
  const running = state.ai_status === "running";

  return (
    <div className="space-y-4 font-mono" data-testid="ai-panel">
      <div className="rounded-md border border-[#5FA82E]/30 bg-[#5FA82E]/5 p-3">
        <p className="text-[11px] leading-relaxed text-[#5FA82E]">&gt; AI Interaction Assistant<br /><span className="text-[#64748B]">Analyses page text and proposes narration, video, quiz & hotspot placements. Nothing changes without your approval.</span></p>
        <button onClick={analyze} disabled={running} data-testid="ai-analyze-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded bg-[#5FA82E] py-2 text-xs font-medium text-black transition-opacity hover:opacity-90 disabled:opacity-60">
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {running ? "Analyzing book…" : state.suggestions.length ? "Re-analyze book" : "Analyze this book"}
        </button>
      </div>
      {state.ai_status === "failed" && <p className="text-xs text-[#A62B8A]" data-testid="ai-error">Analysis failed. Try again.</p>}
      {state.ai_status === "done" && <p className="text-[11px] text-[#64748B]" data-testid="ai-summary">{pending.length} pending · {state.suggestions.length - pending.length} reviewed</p>}
      {pending.map((s) => {
        const Icon = TYPE_META[ICON_TYPE[s.type] || s.type]?.icon || Sparkles;
        return (
          <div key={s.id} className="rounded-2xl neu-raised p-4" data-testid={`ai-suggestion-${s.id}`}>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#64748B]">
              <span className="flex items-center gap-1.5"><Icon className="h-3 w-3 text-[#5FA82E]" />Page {s.page} · {s.type}</span>
              <span className="flex items-center gap-1.5"><Stars n={s.confidence} /><span className="text-[#5FA82E]">{s.confidence}%</span></span>
            </div>
            <p className="mt-2 font-sans text-sm font-medium text-[#0F172A]">{s.title}</p>
            <p className="mt-1 font-sans text-xs text-[#64748B]">{s.reason}</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => mark(s, "accepted")} data-testid={`ai-accept-${s.id}`} className="flex flex-1 items-center justify-center gap-1 rounded border border-[#5FA82E]/50 py-1.5 text-[11px] text-[#5FA82E] hover:bg-[#5FA82E]/10"><Check className="h-3 w-3" />Accept</button>
              <button onClick={() => mark(s, "rejected")} data-testid={`ai-reject-${s.id}`} className="flex flex-1 items-center justify-center gap-1 rounded border border-[#DCE3EE] py-1.5 text-[11px] text-[#64748B] hover:border-[#A62B8A] hover:text-[#A62B8A]"><X className="h-3 w-3" />Reject</button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
